import { useState, useCallback, useMemo, useRef } from 'react';
import { StorageService } from '../../services/storage';
import { Icons } from '../Icons';

// Widget configuration
export interface WidgetConfig {
  id: string;
  title: string;
  icon?: React.ComponentType<{ size?: number }>;
  defaultCols?: number; // 1-12 grid columns
  visible?: boolean;
}

// Layout item with position and size
interface LayoutItem {
  id: string;
  order: number;
  cols: number; // 1-12
}

type Layout = LayoutItem[];

interface DashboardGridProps {
  widgets: WidgetConfig[];
  children: React.ReactNode[];
  storageKey?: string;
  isEditMode: boolean;
}

export function DashboardGrid({
  widgets,
  children,
  storageKey = 'dashboard_layout',
  isEditMode,
}: DashboardGridProps) {
  // Load saved layout or generate default
  const [layout, setLayout] = useState<Layout>(() => {
    const saved = StorageService.get<Layout>(storageKey);
    if (saved && saved.length === widgets.length) {
      return saved;
    }
    return widgets.map((w, index) => ({
      id: w.id,
      order: index,
      cols: w.defaultCols || 6,
    }));
  });

  const dragItem = useRef<string | null>(null);
  const dragOverItem = useRef<string | null>(null);

  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, widgetId: string) => {
    dragItem.current = widgetId;
    e.dataTransfer.effectAllowed = 'move';
    // Add drag styling
    (e.target as HTMLElement).classList.add('dragging');
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback((e: React.DragEvent) => {
    (e.target as HTMLElement).classList.remove('dragging');

    if (dragItem.current && dragOverItem.current && dragItem.current !== dragOverItem.current) {
      setLayout(prev => {
        const newLayout = [...prev];
        const dragIdx = newLayout.findIndex(l => l.id === dragItem.current);
        const dropIdx = newLayout.findIndex(l => l.id === dragOverItem.current);

        if (dragIdx !== -1 && dropIdx !== -1) {
          // Swap orders
          const temp = newLayout[dragIdx].order;
          newLayout[dragIdx].order = newLayout[dropIdx].order;
          newLayout[dropIdx].order = temp;

          // Sort by order
          newLayout.sort((a, b) => a.order - b.order);

          // Save to storage
          StorageService.set(storageKey, newLayout);
        }

        return newLayout;
      });
    }

    dragItem.current = null;
    dragOverItem.current = null;
  }, [storageKey]);

  // Handle drag over
  const handleDragOver = useCallback((e: React.DragEvent, widgetId: string) => {
    e.preventDefault();
    dragOverItem.current = widgetId;
  }, []);

  // Handle column resize
  const handleResize = useCallback((widgetId: string, newCols: number) => {
    setLayout(prev => {
      const newLayout = prev.map(item =>
        item.id === widgetId ? { ...item, cols: Math.max(3, Math.min(12, newCols)) } : item
      );
      StorageService.set(storageKey, newLayout);
      return newLayout;
    });
  }, [storageKey]);

  // Sort widgets by order
  const sortedWidgets = useMemo(() => {
    const visibleWidgets = widgets.filter(w => w.visible !== false);
    return visibleWidgets
      .map(widget => {
        const layoutItem = layout.find(l => l.id === widget.id);
        return {
          widget,
          layoutItem: layoutItem || { id: widget.id, order: 999, cols: widget.defaultCols || 6 },
          childIndex: widgets.findIndex(w => w.id === widget.id),
        };
      })
      .sort((a, b) => a.layoutItem.order - b.layoutItem.order);
  }, [widgets, layout]);

  const childrenArray = Array.isArray(children) ? children : [children];

  return (
    <div className={`dashboard-grid-container ${isEditMode ? 'edit-mode' : ''}`}>
      <div className="dashboard-grid">
        {sortedWidgets.map(({ widget, layoutItem, childIndex }) => (
          <div
            key={widget.id}
            className={`dashboard-widget-wrapper`}
            style={{
              gridColumn: `span ${layoutItem.cols}`,
            }}
            draggable={isEditMode}
            onDragStart={(e) => handleDragStart(e, widget.id)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, widget.id)}
          >
            {isEditMode && (
              <div className="widget-edit-header">
                <div className="widget-drag-handle">
                  <Icons.Move size={14} />
                  <span>{widget.title}</span>
                </div>
                <div className="widget-resize-controls">
                  <button
                    onClick={() => handleResize(widget.id, layoutItem.cols - 3)}
                    disabled={layoutItem.cols <= 3}
                    title="Réduire"
                  >
                    <Icons.Minus size={12} />
                  </button>
                  <span className="cols-indicator">{layoutItem.cols}/12</span>
                  <button
                    onClick={() => handleResize(widget.id, layoutItem.cols + 3)}
                    disabled={layoutItem.cols >= 12}
                    title="Agrandir"
                  >
                    <Icons.Plus size={12} />
                  </button>
                </div>
              </div>
            )}
            <div className="widget-content">
              {childrenArray[childIndex]}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .dashboard-grid-container {
          width: 100%;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 16px;
        }

        .dashboard-widget-wrapper {
          min-width: 0;
          transition: all 0.2s ease;
        }

        .edit-mode .dashboard-widget-wrapper {
          border: 2px dashed var(--border);
          border-radius: 12px;
          background: var(--bg-secondary);
          padding: 0;
        }

        .dashboard-widget-wrapper.dragging {
          opacity: 0.5;
          transform: scale(0.98);
        }

        .widget-edit-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 80%, black) 100%);
          color: white;
          border-radius: 10px 10px 0 0;
          font-size: 12px;
        }

        .widget-drag-handle {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: grab;
          font-weight: 600;
        }

        .widget-drag-handle:active {
          cursor: grabbing;
        }

        .widget-resize-controls {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .widget-resize-controls button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 4px;
          background: rgba(255,255,255,0.1);
          color: white;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .widget-resize-controls button:hover:not(:disabled) {
          background: rgba(255,255,255,0.2);
        }

        .widget-resize-controls button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .cols-indicator {
          font-size: 11px;
          font-weight: 500;
          min-width: 32px;
          text-align: center;
        }

        .widget-content {
          padding: 0;
        }

        .edit-mode .widget-content {
          padding: 12px;
        }

        /* Responsive: stack on smaller screens */
        @media (max-width: 1024px) {
          .dashboard-grid {
            grid-template-columns: repeat(6, 1fr);
          }
          .dashboard-widget-wrapper {
            grid-column: span 6 !important;
          }
        }

        @media (max-width: 640px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
          .dashboard-widget-wrapper {
            grid-column: span 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

// Reset layout to defaults
export function resetDashboardLayout(storageKey: string, widgets: WidgetConfig[]): Layout {
  const defaultLayout = widgets.map((w, index) => ({
    id: w.id,
    order: index,
    cols: w.defaultCols || 6,
  }));
  StorageService.set(storageKey, defaultLayout);
  return defaultLayout;
}
